import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { getLevelData } from '../../utils/cbcUtils';
import {
    ArrowLeftIcon,
    AcademicCapIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    ChartBarIcon,
    ChevronDownIcon,
    StarIcon,
    DocumentTextIcon,
    IdentificationIcon,
    ClockIcon,
    DocumentArrowDownIcon,
    ClipboardDocumentListIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    TrophyIcon,
    ExclamationTriangleIcon,
    ScaleIcon
} from '@heroicons/react/24/outline';
import ExportReportModal from '../../components/common/ExportReportModal';
import FilterDropdown from '../../components/common/FilterDropdown';
import PremiumTooltip from '../../components/common/PremiumTooltip';

const getGradeColor = (level) => {
    const data = getLevelData(level);
    return `${data.bg} ${data.color} ${data.border}`;
};

const OutcomeItem = ({ outcome, fallbackLevel }) => {
    const level = outcome.competency_level || fallbackLevel;
    const formattedDate = outcome.assessment_date
        ? new Date(outcome.assessment_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;

    return (
        <div className="flex items-start justify-between p-3.5 bg-white rounded-2xl border border-slate-100 hover:border-slate-200 transition-all shadow-xs gap-3">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {outcome.code && (
                        <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-mono text-[9px] font-black uppercase tracking-wider border border-indigo-100/80">
                            {outcome.code}
                        </span>
                    )}
                    {formattedDate && (
                        <span className="text-[9px] font-bold text-slate-400">
                            {formattedDate}
                        </span>
                    )}
                </div>
                <p className="text-xs font-bold text-slate-700 leading-snug">
                    {outcome.outcome || outcome.description}
                </p>
            </div>
            {level && (
                <div className={`px-2.5 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest flex-shrink-0 shadow-xs ${getGradeColor(level)}`}>
                    {level}
                </div>
            )}
        </div>
    );
};

const SubStrandSection = ({ subStrand, strandLevel }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const outcomes = subStrand.outcomes || [];
    const level = subStrand.competency_level || strandLevel;

    return (
        <div className="bg-slate-50/70 rounded-2xl border border-slate-200/60 p-4 transition-all">
            <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        {subStrand.code && (
                            <span className="text-[9px] font-mono font-black text-slate-400 uppercase tracking-widest">
                                {subStrand.code}
                            </span>
                        )}
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                            Sub-Strand
                        </span>
                    </div>
                    <h5 className="text-xs font-black text-[#18216D] uppercase tracking-tight truncate">
                        {subStrand.name}
                    </h5>
                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">
                        {outcomes.length} {outcomes.length === 1 ? 'Outcome' : 'Outcomes'} Tested
                        {subStrand.percentage != null ? ` • ${Math.round(subStrand.percentage)}%` : ''}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {level && (
                        <span className={`px-2.5 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest ${getGradeColor(level)}`}>
                            {level}
                        </span>
                    )}
                    {outcomes.length > 0 && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1 rounded-lg hover:bg-slate-200/60 text-slate-500 transition-colors"
                            aria-label="Toggle sub-strand outcomes"
                        >
                            <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                    )}
                </div>
            </div>

            {outcomes.length > 0 && isExpanded && (
                <div className="mt-3 space-y-2 animate-in fade-in duration-200">
                    {outcomes.map((outcome, idx) => (
                        <OutcomeItem key={outcome.id || idx} outcome={outcome} fallbackLevel={level} />
                    ))}
                </div>
            )}
        </div>
    );
};

const StrandCard = ({ strand }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const subStrands = strand.sub_strands || [];
    const totalOutcomes = subStrands.reduce((acc, sub) => acc + (sub.outcomes?.length || 0), 0);
    const rubric = strand.assessment_rubric;

    const rubricDescriptor = useMemo(() => {
        if (!rubric || !strand.competency_level) return null;
        const key = `criteria_${strand.competency_level.toLowerCase()}`;
        return rubric[key] || null;
    }, [rubric, strand.competency_level]);

    return (
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-lg hover:shadow-indigo-900/5 transition-all overflow-hidden group">
            {/* Strand Header */}
            <div className="p-6 md:p-7">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className="text-[9px] font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                                Strand
                            </span>
                            {strand.score != null && strand.max_score != null && (
                                <span className="text-[10px] font-black text-slate-600 bg-slate-100/90 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                                    Raw Mark: <span className="text-[#18216D] font-extrabold">{strand.score}</span> / {strand.max_score} pts
                                </span>
                            )}
                            {strand.percentage != null && (
                                <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    {Math.round(strand.percentage)}%
                                </span>
                            )}
                        </div>

                        <h4 className="text-base md:text-lg font-black text-[#18216D] tracking-tight group-hover:text-blue-600 transition-colors uppercase leading-snug">
                            {strand.name}
                        </h4>

                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                            {subStrands.length} Assessed Sub-Strands • {totalOutcomes} Tested Outcomes
                        </p>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Circular percentage mini gauge */}
                        {strand.percentage != null && (
                            <div className="relative h-14 w-14 flex-shrink-0 hidden sm:block">
                                <svg className="h-full w-full rotate-[-90deg]">
                                    <circle cx="28" cy="28" r="23" fill="transparent" stroke="#f1f5f9" strokeWidth="5" />
                                    <circle
                                        cx="28" cy="28" r="23" fill="transparent"
                                        stroke={strand.percentage >= 70 ? '#10B981' : strand.percentage >= 50 ? '#3B82F6' : strand.percentage >= 30 ? '#F59E0B' : '#EF4444'}
                                        strokeWidth="5"
                                        strokeDasharray={`${2 * Math.PI * 23}`}
                                        strokeDashoffset={`${2 * Math.PI * 23 * (1 - Math.min(Math.max(strand.percentage, 0), 100) / 100)}`}
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-[#18216D]">
                                    {Math.round(strand.percentage)}%
                                </div>
                            </div>
                        )}

                        {strand.competency_level && (
                            <div className={`px-3 py-1.5 rounded-xl border text-xs font-black uppercase tracking-widest shadow-xs ${getGradeColor(strand.competency_level)}`}>
                                {strand.competency_level}
                            </div>
                        )}

                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-2 rounded-xl text-[#18216D] hover:bg-slate-100 transition-colors"
                            aria-label="Toggle strand details"
                        >
                            <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Rubric Criteria Snippet if present */}
                {rubricDescriptor && (
                    <div className="mt-4 p-3 bg-amber-50/60 rounded-xl border border-amber-200/50 text-[11px] text-amber-900 leading-relaxed">
                        <span className="font-black uppercase tracking-wider text-[9px] text-amber-800 block mb-0.5">
                            Rubric Criterion ({strand.competency_level}):
                        </span>
                        {rubricDescriptor}
                    </div>
                )}
            </div>

            {/* Expandable Sub-Strands (Tier 3) */}
            <div className={`grid-rows-transition ${isExpanded ? 'grid-rows-expanded' : ''}`}>
                <div className="overflow-hidden">
                    <div className="px-6 md:px-7 pb-6 pt-2 border-t border-slate-50 space-y-3">
                        {subStrands.length > 0 ? (
                            subStrands.map((sub, idx) => (
                                <SubStrandSection
                                    key={sub.id || idx}
                                    subStrand={sub}
                                    strandLevel={strand.competency_level}
                                />
                            ))
                        ) : (
                            <div className="p-4 bg-slate-50 rounded-2xl text-center text-xs font-bold text-slate-400">
                                This strand was evaluated holistically with raw marks directly.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const LearningAreaAccordion = ({ area }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const strands = area.strands || [];
    const totalStrands = strands.length;
    const totalSubStrands = strands.reduce((acc, strand) => acc + (strand.sub_strands?.length || 0), 0);
    const totalOutcomesArea = strands.reduce((acc, strand) =>
        acc + (strand.sub_strands?.reduce((s_acc, sub) => s_acc + (sub.outcomes?.length || 0), 0) || 0), 0);

    return (
        <div className="space-y-4">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-6 bg-white rounded-[2rem] hover:bg-slate-50 transition-all border border-slate-100 shadow-sm"
            >
                <div className="flex items-center gap-4 md:gap-6 text-left">
                    <div>
                        <span className="text-[8px] font-black text-[#FFC425] uppercase tracking-[0.2em] px-2 py-0.5 bg-white rounded mb-1 inline-block leading-none border border-[#FFC425]/20">
                            {area.code}
                        </span>
                        <h3 className="text-lg md:text-xl font-black text-[#18216D] tracking-tight">{area.name}</h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                            {totalStrands} Assessed Strands • {totalSubStrands} Sub-Strands • {totalOutcomesArea} Outcomes
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 md:gap-6">
                    {area.area_competency_level && (
                        <span className={`hidden sm:inline-block px-3 py-1.5 rounded-xl border text-xs font-black uppercase tracking-widest ${getGradeColor(area.area_competency_level)}`}>
                            {area.area_competency_level}
                        </span>
                    )}
                    <div className="bg-[#18216D] text-white px-5 py-3 md:px-6 md:py-4 rounded-[2rem] text-center shadow-xl shadow-indigo-900/20 min-w-[90px]">
                        <p className="text-[8px] font-black text-indigo-300 uppercase tracking-[0.2em] mb-1">Mean Score</p>
                        <p className="text-lg md:text-xl font-black">
                            {area.area_mean_score != null ? area.area_mean_score.toFixed(2) : 'N/A'}
                        </p>
                    </div>
                    <div className={`p-2 rounded-xl bg-slate-50 border border-slate-100 text-[#18216D] transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDownIcon className="w-5 h-5" />
                    </div>
                </div>
            </button>

            <div className={`grid-rows-transition ${isExpanded ? 'grid-rows-expanded' : ''}`}>
                <div className="overflow-hidden">
                    <div className="space-y-4 pb-6 mt-4">
                        {strands.length > 0 ? (
                            strands.map((strand, idx) => (
                                <StrandCard key={strand.id || idx} strand={strand} />
                            ))
                        ) : (
                            <div className="p-8 text-center bg-white rounded-[2rem] border border-slate-100 text-slate-400 font-bold text-xs uppercase tracking-wider">
                                No assessed strands found for this learning area in the selected period.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DEPORTMENT_CATEGORIES = [
    'Grooming & Appearance',
    'Punctuality',
    'Courtesy & Respect',
    'Participation',
    'Behaviour',
];

const StudentDetailsView = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { get, patch } = useApi();
    const { showToast } = useAppState();

    const { studentIds, returnUrl, defaultTab } = location.state || {};
    const searchParams = new URLSearchParams(location.search);
    const queryTab = searchParams.get('tab');
    const currentIndex = studentIds ? studentIds.indexOf(parseInt(studentId)) : -1;
    const hasNext = currentIndex !== -1 && currentIndex < studentIds.length - 1;
    const hasPrev = currentIndex > 0;

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(queryTab || defaultTab || location.state?.activeTab || 'history'); // 'history' | 'mastery' | 'report' | 'character'
    const [activeParentType, setActiveParentType] = useState(null); // 'FATHER' | 'MOTHER'
    const [showFilters, setShowFilters] = useState(false);
    const [characterTypeFilter, setCharacterTypeFilter] = useState('all'); // 'all' | 'merit' | 'demerit'
    const [characterRecords, setCharacterRecords] = useState([]);
    const [characterLoading, setCharacterLoading] = useState(false);

    const [termReport, setTermReport] = useState(null);
    const [termReportLoading, setTermReportLoading] = useState(false);
    const [termReportSaving, setTermReportSaving] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    
    const [deportmentForm, setDeportmentForm] = useState(
        DEPORTMENT_CATEGORIES.map(cat => ({ category: cat, rating: 'ME' }))
    );
    const [narrativeForm, setNarrativeForm] = useState({
        core_competencies: [],
        values: [],
        status: 'DRAFT',
        responsibilities: '',
        cocurricular_activities: '',
        social_work_hours: 0,
        deportment_grade: 'ME',
        absent_days: 0
    });

    // Core Filters
    const [searchValue, setSearchValue] = useState('');
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [terms, setTerms] = useState([]);
    const [selectedTerm, setSelectedTerm] = useState('');
    const [activeTermId, setActiveTermId] = useState(null);

    const fetchData = useCallback(async (termId) => {
        try {
            setLoading(true);
            let url = `/api/teacher/class-teacher/student_performance_report/${studentId}/`;
            if (termId) url += `?term_id=${termId}`;
            const response = await get(url);
            setData(response);

            // Set initial parent tab
            const parents = response.profile?.guardians || [];
            if (parents.length > 0) {
                const hasFather = parents.some(p => p.relationship === 'FATHER');
                setActiveParentType(hasFather ? 'FATHER' : parents[0].relationship);
            }
        } catch (error) {
            console.error('Error fetching student report:', error);
            showToast('Failed to load student performance report', 'error');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    }, [studentId, get, showToast, navigate]);

    // Clear old data when cycling students so the loader shows immediately
    useEffect(() => {
        setData(null);
        setTermReport(null);
        setCharacterRecords([]);
    }, [studentId]);

    const fetchCharacterRecords = useCallback(async (termId) => {
        if (!studentId) return;
        try {
            setCharacterLoading(true);
            let url = `/api/discipline/records/?student_id=${studentId}`;
            const targetTerm = termId !== undefined ? termId : selectedTerm;
            if (targetTerm) url += `&term_id=${targetTerm}`;
            const res = await get(url);
            setCharacterRecords(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error("Failed to load student character records:", err);
            setCharacterRecords([]);
        } finally {
            setCharacterLoading(false);
        }
    }, [studentId, selectedTerm, get]);

    useEffect(() => {
        if (activeTab === 'character') {
            fetchCharacterRecords();
        }
    }, [activeTab, fetchCharacterRecords]);

    const fetchTermReport = useCallback(async (termId) => {
        if (!termId) return;
        try {
            setTermReportLoading(true);
            const tr = await get(`/api/cbc/reports/student/${studentId}/term/${termId}/`);
            setTermReport(tr);
            if (tr) {
                setDeportmentForm(tr.deportment?.length ? tr.deportment
                    : DEPORTMENT_CATEGORIES.map(cat => ({ category: cat, rating: 'ME' })));
                setNarrativeForm({
                    core_competencies: tr.core_competencies || [],
                    values: tr.values || [],
                    status: tr.status || 'DRAFT',
                    responsibilities: tr.responsibilities || '',
                    cocurricular_activities: tr.cocurricular_activities || '',
                    social_work_hours: tr.social_work_hours || 0,
                    deportment_grade: tr.deportment_grade || 'ME',
                    absent_days: tr.absent_days || 0
                });
            }
        } catch {
            setTermReport(null);
        } finally {
            setTermReportLoading(false);
        }
    }, [studentId, get]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveTermReport = async () => {
        if (!selectedTerm) { showToast('Please select a term first', 'error'); return; }
        try {
            setTermReportSaving(true);
            const payload = { ...narrativeForm, deportment: deportmentForm };
            await patch(`/api/cbc/reports/student/${studentId}/term/${selectedTerm}/`, payload);
            showToast('Term report saved successfully!', 'success');
            fetchTermReport(selectedTerm);
        } catch {
            showToast('Failed to save term report', 'error');
        } finally {
            setTermReportSaving(false);
        }
    };

    // Removed inline handleExportReport    // Initial load: Years, Terms and Active Term
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [termsData, yearsData, activeTermData] = await Promise.all([
                    get('/api/academic-terms/'),
                    get('/api/academic-years/'),
                    get('/api/auth/active-term/')
                ]);
                setTerms(termsData || []);
                setAcademicYears(yearsData || []);

                const activeId = activeTermData?.term?.id;
                setActiveTermId(activeId);

                if (activeId) {
                    const activeTerm = termsData.find(t => t.id === activeId);
                    if (activeTerm) {
                        setSelectedYear(activeTerm.year);
                        setSelectedTerm(activeId);
                        fetchData(activeId);
                        fetchTermReport(activeId);
                        return;
                    }
                }

                if (yearsData?.length > 0) {
                    const currentYear = yearsData.find(_y => _y.is_current) || yearsData[0];
                    setSelectedYear(currentYear.id);
                    fetchData(); // Load default
                }
            } catch (err) {
                console.error("Failed to load period context:", err);
                fetchData(); // Fallback
            }
        };
        loadInitialData();
    }, [get, fetchData, fetchTermReport]);

    // Re-fetch when term changes
    const handleTermChange = (newTerm) => {
        setSelectedTerm(newTerm);
        setData(null);
        setTermReport(null);
        fetchData(newTerm);
        fetchTermReport(newTerm);
        if (activeTab === 'character') {
            fetchCharacterRecords(newTerm);
        }
    };

    const characterStats = useMemo(() => {
        const totalMerits = characterRecords.filter(r => r.category_type === 'merit').reduce((sum, r) => sum + (r.points || 0), 0);
        const totalDemerits = characterRecords.filter(r => r.category_type === 'demerit').reduce((sum, r) => sum + (r.points || 0), 0);
        const netPoints = totalMerits - totalDemerits;
        const meritCount = characterRecords.filter(r => r.category_type === 'merit').length;
        const demeritCount = characterRecords.filter(r => r.category_type === 'demerit').length;
        
        let standing = 'Good Standing';
        if (netPoints >= 15 && demeritCount === 0) {
            standing = 'Exemplary';
        } else if (totalDemerits >= 15 || demeritCount >= 5) {
            standing = 'Critical';
        } else if (totalDemerits >= 6 || demeritCount >= 3) {
            standing = 'Under Watch';
        }
        return { totalMerits, totalDemerits, netPoints, meritCount, demeritCount, standing };
    }, [characterRecords]);

    const filteredCharacterRecords = useMemo(() => {
        return characterRecords.filter(rec => {
            if (characterTypeFilter !== 'all' && rec.category_type !== characterTypeFilter) return false;
            if (searchValue) {
                const q = searchValue.toLowerCase();
                const matchCat = rec.category_name?.toLowerCase().includes(q);
                const matchDesc = rec.description?.toLowerCase().includes(q);
                const matchTeacher = rec.logged_by_name?.toLowerCase().includes(q);
                const matchCourse = (rec.learning_area_name || rec.course_name || '')?.toLowerCase().includes(q);
                if (!matchCat && !matchDesc && !matchTeacher && !matchCourse) return false;
            }
            return true;
        });
    }, [characterRecords, characterTypeFilter, searchValue]);

    const specialities = useMemo(() => {
        if (!data?.mastery_report?.learning_areas) return [];
        return [...data.mastery_report.learning_areas]
            .filter(area => (area.breakdown?.EE || 0) > 0)
            .sort((a, b) => (b.breakdown?.EE || 0) - (a.breakdown?.EE || 0))
            .slice(0, 3);
    }, [data]);

    const filteredHistory = useMemo(() => {
        if (!data?.performance_history) return [];
        const s = searchValue.toLowerCase();
        return data.performance_history.filter(h =>
            h.learning_area_name.toLowerCase().includes(s) ||
            h.title.toLowerCase().includes(s)
        );
    }, [data, searchValue]);

    const filteredMastery = useMemo(() => {
        if (!data?.mastery_report?.learning_areas) return [];
        const s = searchValue.toLowerCase();
        return data.mastery_report.learning_areas.filter(area =>
            area.name.toLowerCase().includes(s) ||
            area.code.toLowerCase().includes(s)
        );
    }, [data, searchValue]);

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-8">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#18216D]/10 border-t-[#18216D] mb-6"></div>
                <p className="text-[#18216D] font-black uppercase tracking-widest text-xs animate-pulse">Syncing Scholar Repository...</p>
            </div>
        );
    }

    if (!data) return null;

    const { profile, summary_stats } = data;

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            {/* Floating Navigation Chevrons */}
            {hasPrev && (
                <button
                    onClick={() => navigate(`/teacher/student-detail/${studentIds[currentIndex - 1]}`, { replace: true, state: location.state })}
                    className="fixed left-4 md:left-6 top-1/2 -translate-y-1/2 z-50 p-2 md:p-2.5 bg-white/80 backdrop-blur shadow-xl shadow-indigo-900/10 rounded-full text-[#18216D] hover:bg-[#18216D] hover:text-white transition-all border border-slate-100 hover:scale-110 active:scale-95"
                    title="Previous Student"
                >
                    <ChevronLeftIcon className="w-5 h-5 md:w-6 md:h-6 stroke-[2.5]" />
                </button>
            )}
            {hasNext && (
                <button
                    onClick={() => navigate(`/teacher/student-detail/${studentIds[currentIndex + 1]}`, { replace: true, state: location.state })}
                    className="fixed right-4 md:right-6 top-1/2 -translate-y-1/2 z-50 p-2 md:p-2.5 bg-white/80 backdrop-blur shadow-xl shadow-indigo-900/10 rounded-full text-[#18216D] hover:bg-[#18216D] hover:text-white transition-all border border-slate-100 hover:scale-110 active:scale-95"
                    title="Next Student"
                >
                    <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6 stroke-[2.5]" />
                </button>
            )}

            {/* Header / Identity Bar */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => returnUrl ? navigate(returnUrl, { state: location.state }) : navigate(-1)}
                            className="p-3 bg-slate-50 rounded-2xl text-slate-400 hover:text-[#18216D] hover:bg-slate-100 transition-all border border-slate-100"
                        >
                            <ArrowLeftIcon className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-black text-[#18216D]/40 uppercase tracking-[0.2em]">Class Teacher Hub</span>
                                <div className="h-1 w-1 bg-slate-200 rounded-full" />
                                <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Scholar Profile Mirror</span>
                            </div>
                            <h1 className="text-2xl font-black text-[#18216D] tracking-tight flex items-center gap-3">
                                {profile.student.full_name}
                                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest rounded-lg border border-indigo-100">
                                    {profile.student.student_id}
                                </span>
                            </h1>
                        </div>
                    </div>

                    {/* Header Export Action */}
                    {selectedTerm && !termReportLoading && (
                        <button
                            onClick={() => setIsExportModalOpen(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-900/20 hover:bg-indigo-700 transition-all disabled:opacity-50"
                        >
                            <DocumentArrowDownIcon className="w-5 h-5" />
                            Export Report Card PDF
                        </button>
                    )}
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6 space-y-8">
                {/* Identity, Guardians & Specialities Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Scholar Identity Card */}
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group h-full">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-[1.5rem] bg-indigo-600 flex items-center justify-center text-white shadow-xl shadow-indigo-900/20">
                                    <IdentificationIcon className="w-8 h-8" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Scholar Details</h3>
                                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">Academic Profile</p>
                                </div>
                            </div>
                            <div className="space-y-4 pt-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                        <AcademicCapIcon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Grade / Stream</p>
                                        <p className="text-xs font-bold text-[#18216D]">{profile.student.grade_name} ({profile.student.stream_name})</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                        <ChartBarIcon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Avg Performance</p>
                                        <p className="text-xs font-bold text-[#18216D]">
                                            {summary_stats.average_performance_level}
                                            {summary_stats.average_performance_numeric > 0 && ` (${summary_stats.average_performance_numeric} MS)`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                        <CalendarIcon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Days Absent</p>
                                        <p className="text-xs font-bold text-[#18216D]">{termReport?.absent_days ?? summary_stats.absent_days ?? '—'} Days</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Guardian Card */}
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group h-full">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-[1.5rem] bg-emerald-600 flex items-center justify-center text-white shadow-xl shadow-emerald-900/20">
                                    <UserIcon className="w-8 h-8" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Guardian Info</h3>
                                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">Primary Contact</p>
                                </div>
                            </div>
                            <div className="space-y-4 pt-4">
                                {(() => {
                                    const parents = profile.guardians || [];
                                    const hasMultiple = parents.length > 1;

                                    // If no parents, fallback to legacy guardian
                                    if (parents.length === 0) {
                                        return (
                                            <>
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                                        <UserIcon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Guardian Name</p>
                                                        <p className="text-xs font-bold text-[#18216D] truncate">{profile.guardian.name}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                                        <EnvelopeIcon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Email Address</p>
                                                        <p className="text-xs font-bold text-[#18216D] truncate">{profile.guardian.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                                        <PhoneIcon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Phone</p>
                                                        <p className="text-xs font-bold text-[#18216D] truncate">{profile.guardian.phone}</p>
                                                    </div>
                                                </div>
                                            </>
                                        );
                                    }

                                    const currentParent = parents.find(p => p.relationship === activeParentType) || parents[0];

                                    return (
                                        <>
                                            {hasMultiple && (
                                                <div className="flex bg-slate-50 p-1 rounded-xl w-full mb-2">
                                                    {['FATHER', 'MOTHER'].map(type => {
                                                        const exists = parents.some(p => p.relationship === type);
                                                        if (!exists) return null;
                                                        return (
                                                            <button
                                                                key={type}
                                                                onClick={() => setActiveParentType(type)}
                                                                className={`flex-1 py-1.5 text-[8px] font-black uppercase tracking-widest rounded-lg transition-all ${activeParentType === type ? 'bg-[#18216D] text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                                            >
                                                                {type}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                                    <UserIcon className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                                        {currentParent.relationship || 'Parent'} Name
                                                    </p>
                                                    <p className="text-xs font-bold text-[#18216D] truncate">{currentParent.name}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                                    <EnvelopeIcon className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Email Address</p>
                                                    <a
                                                        href={`mailto:${currentParent.email}`}
                                                        className="text-xs font-bold text-[#18216D] truncate hover:text-indigo-600 transition-colors block"
                                                    >
                                                        {currentParent.email}
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                                    <PhoneIcon className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">WhatsApp</p>
                                                    <a
                                                        href={`https://wa.me/${currentParent.phone?.replace(/\D/g, '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs font-bold truncate font-mono text-emerald-600 hover:text-emerald-700 transition-colors block"
                                                    >
                                                        {currentParent.phone || 'No phone'}
                                                    </a>
                                                </div>
                                            </div>

                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Speciality Card */}
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group h-full">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-[1.5rem] bg-amber-500 flex items-center justify-center text-white shadow-xl shadow-amber-900/20">
                                    <StarIcon className="w-8 h-8" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Specialities</h3>
                                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">Top Strengths</p>
                                </div>
                            </div>
                            <div className="space-y-4 pt-4">
                                {specialities.length > 0 ? specialities.map((area, idx) => (
                                    <div key={area.code} className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 font-black text-xs">
                                            #{idx + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{area.code}</p>
                                            <p className="text-xs font-bold text-[#18216D] truncate">{area.name}</p>
                                            <p className="text-[8px] font-black text-amber-500 uppercase tracking-widest">{area.breakdown?.EE || 0} EE Outcomes</p>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="py-6 text-center">
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No core specialities identified yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Underline Tabs Matching Lesson Records Component */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-200 mb-6 gap-4">
                    <nav className="flex space-x-6 sm:space-x-8 overflow-x-auto pb-1 sm:pb-0" aria-label="Scholar Profile Tabs">
                        <button
                            type="button"
                            onClick={() => setActiveTab('mastery')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 whitespace-nowrap ${
                                activeTab === 'mastery'
                                    ? 'text-[#18216D] border-[#18216D]'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                            }`}
                        >
                            <AcademicCapIcon className={`w-4 h-4 ${activeTab === 'mastery' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Mastery Matrix</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('history')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 whitespace-nowrap ${
                                activeTab === 'history'
                                    ? 'text-[#18216D] border-[#18216D]'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                            }`}
                        >
                            <ClockIcon className={`w-4 h-4 ${activeTab === 'history' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Perf History</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('report')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 whitespace-nowrap ${
                                activeTab === 'report'
                                    ? 'text-[#18216D] border-[#18216D]'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                            }`}
                        >
                            <DocumentTextIcon className={`w-4 h-4 ${activeTab === 'report' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Term Report</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('character')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 whitespace-nowrap ${
                                activeTab === 'character'
                                    ? 'text-[#18216D] border-[#18216D]'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                            }`}
                        >
                            <ScaleIcon className={`w-4 h-4 ${activeTab === 'character' ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Character</span>
                            {characterRecords.length > 0 && (
                                <span className="ml-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-50 text-[#18216D] border border-indigo-100">
                                    {characterRecords.length}
                                </span>
                            )}
                        </button>
                    </nav>

                    {/* Pop-Out Filters Trigger & Universal Search Input */}
                    <div className="flex items-center gap-3 pb-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 border transition-all ${
                                showFilters || selectedYear || (selectedTerm && selectedTerm !== activeTermId) || (activeTab === 'character' && characterTypeFilter !== 'all')
                                    ? 'bg-[#18216D] text-white border-[#18216D] shadow-sm'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-3.5 h-3.5" />
                            <span>Filters</span>
                            {(selectedYear || (selectedTerm && selectedTerm !== activeTermId) || (activeTab === 'character' && characterTypeFilter !== 'all')) && (
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            )}
                        </button>

                        {/* Universal Search Input following the new design */}
                        <div className="relative group flex-1 sm:w-64">
                            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                <MagnifyingGlassIcon className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                            />
                        </div>
                    </div>
                </div>

                {/* Pop-Out Filters Drawer Below Tabs */}
                {showFilters && (
                    <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 mb-6 animate-fadeIn">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                            <div className="flex items-center gap-2">
                                <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                                <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                    Tab Filters ({activeTab === 'mastery' ? 'Mastery Matrix' : activeTab === 'history' ? 'Perf History' : activeTab === 'report' ? 'Term Report' : 'Character'})
                                </span>
                                {(selectedYear || (selectedTerm && selectedTerm !== activeTermId) || (activeTab === 'character' && characterTypeFilter !== 'all')) && (
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                        Filtered
                                    </span>
                                )}
                            </div>
                            {(selectedYear || (selectedTerm && selectedTerm !== activeTermId) || (activeTab === 'character' && characterTypeFilter !== 'all')) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedYear('');
                                        setSelectedTerm(activeTermId || '');
                                        setCharacterTypeFilter('all');
                                        fetchData(activeTermId);
                                        fetchTermReport(activeTermId);
                                    }}
                                    className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                                >
                                    Reset Filters
                                </button>
                            )}
                        </div>

                        <div className={`grid grid-cols-1 ${activeTab === 'character' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
                            {/* Year Filter */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                    Academic Year
                                </label>
                                <FilterDropdown
                                    value={selectedYear}
                                    onChange={(val) => {
                                        setSelectedYear(val);
                                        setSelectedTerm('');
                                        fetchData();
                                    }}
                                    options={academicYears.map(year => ({ name: year.name, value: year.id }))}
                                    placeholder="Select Year"
                                    className="w-full"
                                />
                            </div>

                            {/* Term Filter */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                    Academic Term
                                </label>
                                <FilterDropdown
                                    value={selectedTerm}
                                    onChange={handleTermChange}
                                    options={terms
                                        .filter(term => !selectedYear || term.year === selectedYear)
                                        .map(term => ({ name: `${term.name} ${term.id === activeTermId ? '(Current)' : ''}`, value: term.id }))}
                                    placeholder="Select Term"
                                    className="w-full"
                                />
                            </div>

                            {/* Type Filter (for Character Tab) */}
                            {activeTab === 'character' && (
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Incident Type
                                    </label>
                                    <FilterDropdown
                                        value={characterTypeFilter}
                                        onChange={(val) => setCharacterTypeFilter(val || 'all')}
                                        options={[
                                            { name: 'All Types', value: 'all' },
                                            { name: 'Merits Only', value: 'merit' },
                                            { name: 'Demerits Only', value: 'demerit' },
                                        ]}
                                        placeholder="All Types"
                                        className="w-full"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Dynamic Content Area */}
                <div className="w-full">
                    {activeTab === 'mastery' && (
                        <div className="space-y-6 animate-in fade-in duration-500">
                            {filteredMastery.length > 0 ? filteredMastery.map((area) => (
                                <LearningAreaAccordion key={area.code} area={area} />
                            )) : (
                                <div className="py-20 text-center bg-white rounded-[2.5rem] border border-slate-100">
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <DocumentTextIcon className="w-10 h-10 text-slate-200" />
                                    </div>
                                    <h4 className="text-lg font-black text-slate-300 uppercase tracking-widest">No matching competencies</h4>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="animate-in fade-in duration-500">
                            <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden flex flex-col h-full">
                                <div className="flex-1 overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-[#F8FAFC]">
                                            <tr>
                                                <th className="px-8 py-6 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Activity / Assessment</th>
                                                <th className="px-8 py-6 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Exam Type</th>
                                                <th className="px-8 py-6 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Learning Area</th>
                                                <th className="px-8 py-6 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Activity Type</th>
                                                <th className="px-8 py-6 text-left text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Result Details</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {filteredHistory.map((item) => (
                                                <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                                                    <td className="px-8 py-6">
                                                        <p className="text-sm font-black text-[#18216D] line-clamp-1">{item.title}</p>
                                                        <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase tracking-tight">{new Date(item.submitted_at).toLocaleDateString(undefined, { dateStyle: 'long' })}</p>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        {item.assessment_type === 'summative' ? (
                                                            <span className="px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest bg-rose-50 text-rose-600 border border-rose-100">
                                                                {item.exam_type ? `Summative (${item.exam_type})` : (item.title.toLowerCase().includes('end') ? 'Summative (End-Term)' : (item.title.toLowerCase().includes('mid') ? 'Summative (Mid-Term)' : 'Summative'))}
                                                            </span>
                                                        ) : (
                                                            <span className="px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-100">
                                                                Formative
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100 italic whitespace-nowrap">
                                                            {item.learning_area_name}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] ${item.type === 'Quiz' ? 'bg-amber-50 text-amber-600 border border-amber-100' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'}`}>
                                                            {item.type}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-6 text-right">
                                                        <div className="flex flex-col items-end gap-1.5">
                                                            {item.competency_level && (
                                                                <span className={`px-4 py-1 rounded-xl text-sm font-black uppercase tracking-[0.2em] border-2 shadow-sm transition-transform group-hover:scale-110 ${getGradeColor(item.competency_level)}`}>
                                                                    {item.competency_level}
                                                                </span>
                                                            )}
                                                            <div className="font-bold text-slate-400 text-[10px] uppercase tracking-widest italic flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                                                {item.score != null ? `${item.score}` : '--'}
                                                                <span className="opacity-60">/ {item.total_marks || 0} Points</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {filteredHistory.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="py-24 text-center">
                                                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                                            <ClockIcon className="w-8 h-8 text-slate-200" />
                                                        </div>
                                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No historical data available for this selection</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </div>
                    )}

                    {/* ── TERM REPORT TAB ───────────────────────────────────────── */}
                    {activeTab === 'report' && (
                        <div className="space-y-6">
                            {/* Status banner */}
                            {termReport && (
                                <div className={`flex items-center gap-3 px-5 py-3 rounded-2xl text-sm font-bold border ${termReport.status === 'FINAL'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                    : termReport.status === 'DRAFT'
                                        ? 'bg-amber-50 text-amber-700 border-amber-100'
                                        : 'bg-slate-50 text-slate-500 border-slate-100'
                                    }`}>
                                    <ClipboardDocumentListIcon className="w-5 h-5" />
                                    End-Term Report status: <span className="uppercase tracking-widest ml-1">{termReport.status}</span>
                                    {termReport.status !== 'FINAL' && <span className="ml-2 text-xs font-normal opacity-70">– This report will show a DRAFT watermark when exported.</span>}
                                </div>
                            )}

                            {!selectedTerm && (
                                <div className="text-center py-16 text-slate-400 font-bold text-sm">
                                    Please select a term above to manage the Term Report.
                                </div>
                            )}

                            {selectedTerm && termReportLoading && (
                                <div className="flex justify-center py-12">
                                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#18216D]/10 border-t-[#18216D]" />
                                </div>
                            )}

                            {selectedTerm && !termReportLoading && (
                                <>
                                    {/* Deportment & Life Skills */}
                                    <section className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 space-y-8">
                                        <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Life Skills & Deportment</h3>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            {/* Singular Deportment Grade */}
                                            <div className="space-y-4">
                                                <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Overall Deportment Grade</label>
                                                <select
                                                    value={narrativeForm.deportment_grade}
                                                    onChange={e => setNarrativeForm(f => ({ ...f, deportment_grade: e.target.value }))}
                                                    className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 font-black text-[10px] uppercase tracking-widest text-[#18216D] focus:ring-2 focus:ring-[#18216D]/10"
                                                >
                                                    <option value="EE">Exceeding Expectations (EE)</option>
                                                    <option value="ME">Meeting Expectations (ME)</option>
                                                    <option value="AE">Approaching Expectations (AE)</option>
                                                    <option value="BE">Below Expectations (BE)</option>
                                                </select>
                                            </div>

                                            {/* Social Work Hours */}
                                            <div className="space-y-4">
                                                <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Social Work Hours</label>
                                                <input
                                                    type="number"
                                                    value={narrativeForm.social_work_hours}
                                                    onChange={e => setNarrativeForm(f => ({ ...f, social_work_hours: parseFloat(e.target.value) || 0 }))}
                                                    className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 font-bold text-sm text-[#18216D] focus:ring-2 focus:ring-[#18216D]/10"
                                                    placeholder="e.g. 15.5"
                                                />
                                            </div>
                                        </div>

                                        {/* Attendance */}
                                        <div className="space-y-4">
                                            <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Days Absent</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.5"
                                                value={narrativeForm.absent_days}
                                                onChange={e => setNarrativeForm(f => ({ ...f, absent_days: parseFloat(e.target.value) || 0 }))}
                                                className="w-full max-w-[200px] px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 font-bold text-sm text-[#18216D] focus:ring-2 focus:ring-[#18216D]/10"
                                                placeholder="e.g. 4.5"
                                            />
                                            <p className="text-[10px] font-bold text-slate-400">
                                                Input the number of days absent. e.g., 0.5 for half a day.
                                            </p>
                                        </div>

                                        {/* Responsibilities */}
                                        <div className="space-y-4">
                                            <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Responsibilities</label>
                                            <textarea
                                                value={narrativeForm.responsibilities}
                                                onChange={e => setNarrativeForm(f => ({ ...f, responsibilities: e.target.value }))}
                                                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 font-bold text-sm text-[#18216D] focus:ring-2 focus:ring-[#18216D]/10 min-h-[100px]"
                                                placeholder="e.g. Class Monitor, Games Captain, etc."
                                            />
                                        </div>

                                        {/* Co-curricular Activities */}
                                        <div className="space-y-4 mt-6">
                                            <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Co-curricular Activities</label>
                                            <textarea
                                                value={narrativeForm.cocurricular_activities}
                                                onChange={e => setNarrativeForm(f => ({ ...f, cocurricular_activities: e.target.value }))}
                                                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 font-bold text-sm text-[#18216D] focus:ring-2 focus:ring-[#18216D]/10 min-h-[100px]"
                                                placeholder="e.g. Football Team, Science Club, Drama Society"
                                            />
                                        </div>
                                    </section>

                                    {/* Multi-select Pickers */}
                                    <section className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 space-y-8">
                                        <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest">Qualitative Assessment</h3>
                                        
                                        {/* Core Competencies */}
                                        <div className="space-y-4">
                                            <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Core Competencies</label>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    'Communication and Collaboration',
                                                    'Self-efficacy',
                                                    'Critical Thinking and Problem Solving',
                                                    'Creativity and Imagination',
                                                    'Citizenship',
                                                    'Digital Literacy',
                                                    'Learning to Learn'
                                                ].map(comp => {
                                                    const isSelected = narrativeForm.core_competencies?.includes(comp);
                                                    return (
                                                        <button
                                                            key={comp}
                                                            onClick={() => {
                                                                const current = narrativeForm.core_competencies || [];
                                                                const next = isSelected 
                                                                    ? current.filter(c => c !== comp)
                                                                    : [...current, comp];
                                                                setNarrativeForm(f => ({ ...f, core_competencies: next }));
                                                            }}
                                                            className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-2 ${
                                                                isSelected 
                                                                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm'
                                                                    : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                                            }`}
                                                        >
                                                            {comp}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Values */}
                                        <div className="space-y-4">
                                            <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest">Values</label>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    'Love',
                                                    'Responsibility',
                                                    'Respect',
                                                    'Unity',
                                                    'Peace',
                                                    'Patriotism',
                                                    'Integrity',
                                                    'Social Justice'
                                                ].map(val => {
                                                    const isSelected = narrativeForm.values?.includes(val);
                                                    return (
                                                        <button
                                                            key={val}
                                                            onClick={() => {
                                                                const current = narrativeForm.values || [];
                                                                const next = isSelected 
                                                                    ? current.filter(v => v !== val)
                                                                    : [...current, val];
                                                                setNarrativeForm(f => ({ ...f, values: next }));
                                                            }}
                                                            className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-2 ${
                                                                isSelected 
                                                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm'
                                                                    : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                                            }`}
                                                        >
                                                            {val}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </section>

                                     {/* Status & save - ONLY FOR CLASS TEACHER */}
                                     {(data.permissions?.is_class_teacher || data.permissions?.is_hod) && (
                                         <section className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                             <div>
                                                 <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">End-Term Report status</label>
                                                 <FilterDropdown
                                                     value={narrativeForm.status}
                                                     onChange={val => setNarrativeForm(f => ({ ...f, status: val }))}
                                                     options={[
                                                         { name: 'Draft', value: 'DRAFT' },
                                                         { name: 'Final', value: 'FINAL' }
                                                     ]}
                                                     placeholder="Select Status"
                                                     up={true}
                                                 />
                                                 <p className="text-[9px] text-slate-400 mt-1">Reports marked as <strong>Final</strong> will not show the draft watermark.</p>
                                             </div>
                                             <button
                                                 onClick={saveTermReport}
                                                 disabled={termReportSaving}
                                                 className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-900/20 hover:bg-emerald-700 transition-all disabled:opacity-50"
                                             >
                                                 {termReportSaving ? 'Saving...' : 'Save Term Report'}
                                             </button>
                                         </section>
                                     )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── 4TH TAB: CHARACTER LEDGER ───────────────────────────── */}
                    {activeTab === 'character' && (
                        <div className="space-y-6 animate-in fade-in duration-500">
                            {/* Character Metrics Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Incidents</p>
                                    <p className="text-2xl font-black text-[#18216D] mt-1">{characterRecords.length}</p>
                                    <p className="text-[10px] font-bold text-slate-400 mt-1">Logged records</p>
                                </div>
                                <div className="bg-white rounded-2xl border border-emerald-100/70 p-5 shadow-sm">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Merits Awarded</p>
                                    <p className="text-2xl font-black text-emerald-600 mt-1">+{characterStats.totalMerits}</p>
                                    <p className="text-[10px] font-bold text-emerald-600/80 mt-1">{characterStats.meritCount} commendations</p>
                                </div>
                                <div className="bg-white rounded-2xl border border-rose-100/70 p-5 shadow-sm">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-rose-600">Demerits Incurred</p>
                                    <p className="text-2xl font-black text-rose-600 mt-1">-{characterStats.totalDemerits}</p>
                                    <p className="text-[10px] font-bold text-rose-600/80 mt-1">{characterStats.demeritCount} infractions</p>
                                </div>
                                <div className="bg-white rounded-2xl border border-indigo-100/70 p-5 shadow-sm">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Net Standing</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <p className={`text-2xl font-black ${characterStats.netPoints >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {characterStats.netPoints > 0 ? `+${characterStats.netPoints}` : characterStats.netPoints}
                                        </p>
                                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                            characterStats.standing === 'Exemplary'
                                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                : characterStats.standing === 'Under Watch'
                                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                                : characterStats.standing === 'Critical'
                                                ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                                : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                        }`}>
                                            {characterStats.standing}
                                        </span>
                                    </div>
                                    <p className="text-[10px] font-bold text-slate-400 mt-1">Net character balance</p>
                                </div>
                            </div>

                            {/* Character Ledger Table */}
                            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                                {characterLoading ? (
                                    <div className="py-20 text-center space-y-3">
                                        <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin mx-auto" />
                                        <p className="text-xs font-black uppercase tracking-widest text-slate-400">Loading Character Ledger...</p>
                                    </div>
                                ) : filteredCharacterRecords.length === 0 ? (
                                    <div className="py-20 text-center">
                                        <ScaleIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                        <h4 className="text-base font-black text-slate-800 uppercase tracking-tight">No Incidents Recorded</h4>
                                        <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                            No merit or demerit entries found for this scholar under the selected criteria.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-slate-50/75 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                    <th className="py-4 px-6">Incident Date</th>
                                                    <th className="py-4 px-6">Type & Category</th>
                                                    <th className="py-4 px-6">Learning Area / Course</th>
                                                    <th className="py-4 px-6 text-center">Points</th>
                                                    <th className="py-4 px-6 text-center">Severity</th>
                                                    <th className="py-4 px-6">Logged By</th>
                                                    <th className="py-4 px-6">Notes / Rationale</th>
                                                    <th className="py-4 px-6 text-right">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 text-xs">
                                                {filteredCharacterRecords.map(record => {
                                                    const isMerit = record.category_type === 'merit';

                                                    return (
                                                        <tr
                                                            key={record.id}
                                                            className={`transition-colors ${
                                                                isMerit ? 'hover:bg-emerald-50/30' : 'hover:bg-rose-50/20'
                                                            }`}
                                                        >
                                                            {/* Date */}
                                                            <td className="py-4 px-6 whitespace-nowrap">
                                                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                                                                    <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                                                                    <span>{new Date(record.created_at || record.logged_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                                </div>
                                                                {record.term_name && (
                                                                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">{record.term_name}</p>
                                                                )}
                                                            </td>

                                                            {/* Type & Category */}
                                                            <td className="py-4 px-6">
                                                                <div className="space-y-1">
                                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                                                        isMerit
                                                                            ? 'bg-emerald-100 text-emerald-800'
                                                                            : 'bg-rose-100 text-rose-800'
                                                                    }`}>
                                                                        {isMerit ? (
                                                                            <>
                                                                                <TrophyIcon className="w-3 h-3 text-amber-500" />
                                                                                Merit
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <ExclamationTriangleIcon className="w-3 h-3" />
                                                                                Demerit
                                                                            </>
                                                                        )}
                                                                    </span>
                                                                    <p className="font-bold text-slate-800 text-xs">
                                                                        {record.category_name}
                                                                    </p>
                                                                </div>
                                                            </td>

                                                            {/* Course / Learning Area */}
                                                            <td className="py-4 px-6">
                                                                <span className="font-bold text-slate-700">
                                                                    {record.learning_area_name || record.course_name || 'General / School-Wide'}
                                                                </span>
                                                            </td>

                                                            {/* Points */}
                                                            <td className="py-4 px-6 text-center whitespace-nowrap">
                                                                <span className={`inline-block px-3 py-1 rounded-xl text-xs font-black ${
                                                                    isMerit
                                                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                                                                }`}>
                                                                    {isMerit ? `+${record.points}` : `-${record.points}`} pts
                                                                </span>
                                                            </td>

                                                            {/* Severity */}
                                                            <td className="py-4 px-6 text-center">
                                                                {isMerit ? (
                                                                    <span className="text-slate-300 font-bold">—</span>
                                                                ) : (
                                                                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                                                        record.severity === 'severe'
                                                                            ? 'bg-rose-600 text-white'
                                                                            : record.severity === 'major'
                                                                            ? 'bg-orange-100 text-orange-800'
                                                                            : record.severity === 'moderate'
                                                                            ? 'bg-amber-100 text-amber-800'
                                                                            : 'bg-slate-100 text-slate-700'
                                                                    }`}>
                                                                        {record.severity}
                                                                    </span>
                                                                )}
                                                            </td>

                                                            {/* Logged By */}
                                                            <td className="py-4 px-6">
                                                                <div className="flex items-center gap-2">
                                                                    <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                                                                    <span className="font-bold text-slate-800 text-xs">
                                                                        {record.logged_by_name || 'Staff Member'}
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* Notes / Rationale with hover Tooltip */}
                                                            <td className="py-4 px-6 max-w-xs">
                                                                {record.description ? (
                                                                    <PremiumTooltip
                                                                        text={record.description}
                                                                        position="top"
                                                                        allowWrap={true}
                                                                        maxWidth="max-w-xs sm:max-w-sm"
                                                                        wrapperClassName="relative block w-full"
                                                                        textClassName="text-xs font-medium text-slate-700 normal-case tracking-normal"
                                                                    >
                                                                        <p className="text-slate-700 font-medium truncate text-xs cursor-pointer hover:text-[#18216D]">
                                                                            {record.description}
                                                                        </p>
                                                                    </PremiumTooltip>
                                                                ) : (
                                                                    <span className="text-slate-300 text-xs italic">No notes</span>
                                                                )}
                                                            </td>

                                                            {/* Status */}
                                                            <td className="py-4 px-6 text-right whitespace-nowrap">
                                                                <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                                                    record.status === 'resolved'
                                                                        ? 'bg-emerald-100 text-emerald-800'
                                                                        : record.status === 'under_action'
                                                                        ? 'bg-blue-100 text-blue-800'
                                                                        : record.status === 'escalated'
                                                                        ? 'bg-purple-100 text-purple-800'
                                                                        : record.status === 'voided'
                                                                        ? 'bg-slate-200 text-slate-500 line-through'
                                                                        : 'bg-slate-100 text-slate-700'
                                                                }`}>
                                                                    {record.status?.replace('_', ' ')}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Export Modal */}
            <ExportReportModal
                studentId={studentId}
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
            />
        </div>
    );
};

export default StudentDetailsView;
